import { Injectable } from '@angular/core';
import { FORBIDDEN_ACTION, Staff, Workspace } from '../../models';
import { StaffUtils } from '../../utils';
import { PrivateServerCallerService } from '../server-caller/private-server-caller.service';
import { ServerCallerService } from '../server-caller/server-caller.service';

@Injectable({
  providedIn: 'root',
})
export class StaffService {
  constructor(
    private privateServerCallerService: PrivateServerCallerService,
    private serverCallerService: ServerCallerService
  ) {}

  public async getAllStaffsOfBoForOnlineBooking(
    boId: number
  ): Promise<Staff[]> {
    const url = `/v1/business-object/${boId}/staffs-online-booking`;

    const response = await this.serverCallerService.get(url);
    return StaffUtils.transformStaffListForFrontend(response?.body);
  }

  public getMatchedWorkspace(staff: Staff, boId: number): Workspace {
    return staff?.workplaces?.find((w) => w.businessObjectId === boId);
  }

  public async getAllStaffs(): Promise<Staff[]> {
    const url = `/v1/staffs`;

    const response = await this.privateServerCallerService.get(url);

    return StaffUtils.transformStaffListForFrontend(response?.body);
  }

  public async getAllStaffsOfBusinessObject(
    businessObjectId: number
  ): Promise<Staff[]> {
    const url = `/v1/business-object/${businessObjectId}/staffs`;

    const response = await this.privateServerCallerService.get(url);

    return StaffUtils.transformStaffListForFrontend(response?.body);
  }

  public async getStaffByToken(staffToken: string): Promise<Staff> {
    const url = `/v1/staff/${staffToken}`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async createNewStaff(staff: Staff): Promise<Staff> {
    const url = `/v1/staff`;

    const response = await this.privateServerCallerService.post(
      url,
      StaffUtils.transformStaffForBackend(staff)
    );

    return response?.body;
  }

  public async createNewStaffForBusinessObject(
    businessObjectId: number,
    staff: Staff
  ): Promise<Staff> {
    const url = `/v1/business-object/${businessObjectId}/staff`;

    const response = await this.privateServerCallerService.post(
      url,
      StaffUtils.transformStaffForBackend(staff)
    );

    return response?.body;
  }

  public async updateStaffById(staff: Staff): Promise<any> {
    const url = `/v1/staff/${staff.id}`;

    const response = await this.privateServerCallerService.put(
      url,
      StaffUtils.transformStaffForBackend(staff)
    );

    return response?.status;
  }

  public async updateStaffByIdAndAddBusinessObject(
    businessObjectId: number,
    staff: Staff
  ): Promise<any> {
    const url = `/v1/business-object/${businessObjectId}/staff/${staff.id}`;

    const response = await this.privateServerCallerService.put(
      url,
      StaffUtils.transformStaffForBackend(staff)
    );

    return response?.status;
  }

  public async removeBusinessObjectFromStaff(
    businessObjectId: number,
    staffId: number
  ): Promise<any> {
    const url = `/v1/business-object/${businessObjectId}/staff/${staffId}`;

    const response = await this.privateServerCallerService.delete(url);

    return response?.status;
  }

  public async deleteStaffById(staffId: number): Promise<any> {
    const url = `/v1/staff/${staffId}`;

    const response = await this.privateServerCallerService.delete(url);

    return response?.status;
  }

  public async createAppLinkForStaff(
    businessObjectId: number,
    staffId: number,
    forbiddenActions: FORBIDDEN_ACTION[] = []
  ): Promise<string> {
    const url = `/v1/business-object/${businessObjectId}/staff/${staffId}/access-app-link`;

    const response = await this.privateServerCallerService.post(
      url,
      forbiddenActions,
      null,
      ServerCallerService.RESPONSE_TYPE.TEXT
    );

    return response?.body;
  }
}
